import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  getInvitationInfo,
  acceptInvitation,
  declineInvitation,
} from '../../components/User/services/authService';

export default function InvitationPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useSelector((state) => state.auth);

  const [invitation, setInvitation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    const fetchInfo = async () => {
      try {
        const data = await getInvitationInfo(token);
        setInvitation(data);
      } catch (err) {
        setError(err.response?.data?.error || 'A meghívó nem található.');
      } finally {
        setLoading(false);
      }
    };
    fetchInfo();
  }, [token]);

  const handleAccept = async () => {
    if (!isAuthenticated) {
      navigate(`/login?redirect=/invitation/${token}`);
      return;
    }

    setProcessing(true);
    setError(null);
    try {
      const data = await acceptInvitation(token);
      setSuccess(data.success || 'Meghívó sikeresen elfogadva! A gyermekek hozzá lettek kapcsolva a fiókodhoz.');
      setInvitation((prev) => ({ ...prev, status: 'accepted', isPending: false }));
    } catch (err) {
      setError(err.response?.data?.error || 'Hiba történt a meghívó elfogadásakor.');
    } finally {
      setProcessing(false);
    }
  };

  const handleDecline = async () => {
    if (!isAuthenticated) {
      navigate(`/login?redirect=/invitation/${token}`);
      return;
    }

    setProcessing(true);
    setError(null);
    try {
      await declineInvitation(token);
      setSuccess('Meghívó elutasítva.');
      setInvitation((prev) => ({ ...prev, status: 'declined', isPending: false }));
    } catch (err) {
      setError(err.response?.data?.error || 'Hiba történt a meghívó elutasításakor.');
    } finally {
      setProcessing(false);
    }
  };

  const getAcceptButtonLabel = () => {
    if (processing) return 'Feldolgozás...';
    if (isAuthenticated) return 'Elfogadom';
    return 'Bejelentkezés az elfogadáshoz';
  };

  const getDeclineButtonLabel = () => {
    if (processing) return 'Feldolgozás...';
    if (isAuthenticated) return 'Elutasítom';
    return 'Bejelentkezés az elutasításhoz';
  };

  if (loading) {
    return (
      <div className="container mt-5 text-center">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Betöltés...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container container__wrap col-12 col-sm-12 col-md-8 col-lg-6 col-xl-4 col-xxl-4 mt-5">
      <div className="card shadow-sm">
        <div className="card-body p-4">
          <h4 className="card-title mb-4 text-center">
            Szülői meghívó
          </h4>

          {error && (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          )}

          {success && (
            <div className="alert alert-success" role="alert">
              {success}
              <div className="mt-3">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => navigate('/profile')}
                >
                  Ugrás a profilomra
                </button>
              </div>
            </div>
          )}

          {invitation && !success && invitation.isPending && (
            <div>
              <div className="mb-3">
                <p>
                  <strong>{invitation.invitingUserName}</strong>
                  {' '}
                  meghívott téged, hogy közösen kezeljétek gyermekeitek adatait.
                </p>
                {invitation.children && invitation.children.length > 0 && (
                  <div className="mb-2">
                    <strong>Érintett gyermek(ek):</strong>
                    <ul className="mb-0 mt-1">
                      {invitation.children.map((child) => (
                        <li key={child.id}>{child.name}</li>
                      ))}
                    </ul>
                  </div>
                )}
                <p>
                  A meghívó elfogadásával hozzáférsz a fenti gyermek(ek) profiljaihoz,
                  és te is tudsz programokra jelentkezni a nevükben.
                </p>
                <p className="text-muted">
                  <small>
                    {'Érvényes: '}
                    {invitation.expiresAt}
                  </small>
                </p>
              </div>

              {!isAuthenticated && (
                <div className="alert alert-info" role="alert">
                  A meghívó elfogadásához vagy elutasításához először be kell
                  jelentkezned a fiókodba.
                </div>
              )}

              <div className="d-flex gap-2 justify-content-center">
                <button
                  type="button"
                  className="btn btn-success"
                  onClick={handleAccept}
                  disabled={processing}
                >
                  {getAcceptButtonLabel()}
                </button>
                <button
                  type="button"
                  className="btn btn-outline-danger"
                  onClick={handleDecline}
                  disabled={processing}
                >
                  {getDeclineButtonLabel()}
                </button>
              </div>
            </div>
          )}

          {invitation && !success && !invitation.isPending && (
            <div className="text-center">
              {invitation.isExpired && (
                <div className="alert alert-warning">
                  Ez a meghívó lejárt.
                </div>
              )}
              {invitation.status === 'accepted' && (
                <div className="alert alert-info">
                  Ez a meghívó már el lett fogadva.
                </div>
              )}
              {invitation.status === 'declined' && (
                <div className="alert alert-secondary">
                  Ez a meghívó el lett utasítva.
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
